import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from '../../../services/notification.service';
import { DoNotDisturbService } from '../../../services/do-not-disturb.service';
import { GameInvitation } from '../../../features/games/models/game.models';

/**
 * Aviso en vivo de una invitación a Pausa Lúdica. Vive en la raíz de la app para aparecer en cualquier
 * página; "Unirme ahora" lleva a Juegos con el código de la sala.
 */
@Component({
  selector: 'app-game-invite-banner',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      *ngIf="invite"
      role="alert"
      style="position: fixed; top: 20px; left: 50%; transform: translateX(-50%); z-index: 99999;"
      class="w-11/12 max-w-md bg-[#162244] border-2 border-[#A6FF00] rounded-2xl p-4 shadow-2xl shadow-[#A6FF00]/40 animate-fade-in"
    >
      <div class="flex items-start justify-between gap-3">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-full bg-[#A6FF00] text-black font-black flex items-center justify-center text-lg shadow">
            🎮
          </div>
          <div>
            <h4 class="text-sm font-black text-white">¡Invitación a Pausa Lúdica!</h4>
            <p class="text-xs text-slate-300">
              <strong class="text-[#A6FF00]">{{ invite.fromName }}</strong> te invita a una partida de
              <strong class="text-[#00F0FF]">{{ invite.gameTitle }}</strong>.
            </p>
          </div>
        </div>
        <button type="button" (click)="dismiss()" aria-label="Descartar invitación" class="text-slate-400 hover:text-white text-base">✕</button>
      </div>

      <div class="flex items-center gap-2 mt-3">
        <button
          type="button"
          (click)="accept()"
          class="flex-1 py-2 rounded-xl text-xs font-black uppercase tracking-wider bg-gradient-to-r from-[#A6FF00] to-[#00F0FF] text-black shadow-md hover:brightness-110 transition-all flex items-center justify-center gap-1.5"
        >
          <span>🚀 Unirme ahora</span>
        </button>
        <button
          type="button"
          (click)="dismiss()"
          class="px-3.5 py-2 rounded-xl text-xs font-semibold bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors"
        >
          Descartar
        </button>
      </div>
    </div>
  `,
})
export class GameInviteBannerComponent implements OnInit, OnDestroy {
  private readonly notifications = inject(NotificationService);
  private readonly dnd = inject(DoNotDisturbService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();
  private timer: ReturnType<typeof setTimeout> | null = null;

  invite: GameInvitation | null = null;

  ngOnInit(): void {
    this.notifications.gameInvite$.pipe(takeUntil(this.destroy$)).subscribe((invite) => {
      // Modo Reunión / No Molestar: la invitación queda en la campana, sin interrumpir.
      if (this.dnd.isActive()) return;
      this.invite = invite;
      this.clearTimer();
      this.timer = setTimeout(() => (this.invite = null), 30000);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearTimer();
  }

  accept(): void {
    const code = this.invite?.roomCode;
    this.dismiss();
    if (code) void this.router.navigateByUrl(`/dashboard/juegos?join=${encodeURIComponent(code)}`);
  }

  dismiss(): void {
    this.invite = null;
    this.clearTimer();
  }

  private clearTimer(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}
